import { useEffect, useMemo, useState } from "react";
import { Chess } from "chess.js";

const PIECE_BASE = "https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett";
const pieceSrc = (cell) => `${PIECE_BASE}/${cell.color}${cell.type.toUpperCase()}.svg`;

function squareName(file, rank) {
  return "abcdefgh"[file] + (rank + 1);
}

/**
 * Interactive ChessUp board. Click-to-move with legal validation via chess.js.
 * props:
 *   fen: string position
 *   orientation: "white" | "black"
 *   interactive: bool
 *   lastMove: {from,to}
 *   onMove: ({from,to,san,lan,fen,color,piece}) => void
 *   resetKey: any (changing it resets internal board to fen)
 */
export default function ChessBoard({
  fen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
  orientation = "white",
  interactive = true,
  onMove,
  resetKey,
}) {
  const [position, setPosition] = useState(fen);
  const [selected, setSelected] = useState(null);
  const [legal, setLegal] = useState([]);
  const [lastMove, setLastMove] = useState(null);

  useEffect(() => {
    setPosition(fen);
    setSelected(null);
    setLegal([]);
    setLastMove(null);
  }, [fen, resetKey]);

  const game = useMemo(() => {
    try { return new Chess(position); } catch { return new Chess(); }
  }, [position]);

  const board = game.board(); // 8x8 from rank8 to rank1

  const files = orientation === "white" ? [0, 1, 2, 3, 4, 5, 6, 7] : [7, 6, 5, 4, 3, 2, 1, 0];
  const ranks = orientation === "white" ? [7, 6, 5, 4, 3, 2, 1, 0] : [0, 1, 2, 3, 4, 5, 6, 7];

  const handleSquare = (sq) => {
    if (!interactive) return;
    if (selected) {
      if (sq === selected) { setSelected(null); setLegal([]); return; }
      // try move
      const g = new Chess(position);
      let move;
      try {
        move = g.move({ from: selected, to: sq, promotion: "q" });
      } catch { move = null; }
      if (move) {
        const newFen = g.fen();
        setPosition(newFen);
        setLastMove({ from: move.from, to: move.to });
        setSelected(null);
        setLegal([]);
        onMove && onMove({
          from: move.from, to: move.to, san: move.san,
          lan: (move.from + move.to + (move.promotion || "")).toLowerCase(),
          fen: newFen, color: move.color, piece: move.piece,
        });
        return;
      }
      // reselect if own piece
      const piece = g.get(sq);
      if (piece && piece.color === g.turn()) {
        selectSquare(sq, g);
      } else {
        setSelected(null); setLegal([]);
      }
    } else {
      const piece = game.get(sq);
      if (piece && piece.color === game.turn()) selectSquare(sq, game);
    }
  };

  const selectSquare = (sq, g) => {
    setSelected(sq);
    const moves = g.moves({ square: sq, verbose: true });
    setLegal(moves.map((m) => m.to));
  };

  return (
    <div className="w-full max-w-[min(92vw,460px)] mx-auto select-none" data-testid="chess-board">
      <div
        className="grid grid-cols-8 rounded-xl overflow-hidden shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
        style={{ border: "3px solid #093249", aspectRatio: "1/1" }}
      >
        {ranks.map((rank) =>
          files.map((file) => {
            const sq = squareName(file, rank);
            const cell = board[7 - rank][file];
            const isDark = (file + rank) % 2 === 0;
            const isSelected = selected === sq;
            const isLegal = legal.includes(sq);
            const isLast = lastMove && (lastMove.from === sq || lastMove.to === sq);
            let bg = isDark ? "#1F6273" : "#D9EEEC";
            if (isLast) bg = isDark ? "#2E8C99" : "#8FE3DA";
            if (isSelected) bg = "#00E5D4";
            return (
              <button
                key={sq}
                data-testid={`square-${sq}`}
                onClick={() => handleSquare(sq)}
                className="relative flex items-center justify-center transition-colors duration-150"
                style={{ background: bg, aspectRatio: "1/1", cursor: interactive ? "pointer" : "default" }}
              >
                {isLegal && !cell && (
                  <span className="absolute w-1/4 h-1/4 rounded-full" style={{ background: "rgba(9,50,73,0.35)" }} />
                )}
                {isLegal && cell && (
                  <span className="absolute inset-1 rounded-full" style={{ boxShadow: "inset 0 0 0 3px rgba(9,50,73,0.45)" }} />
                )}
                {cell && (
                  <img
                    src={pieceSrc(cell)}
                    alt={cell.color === "w" ? "peça branca" : "peça preta"}
                    draggable={false}
                    className="relative pointer-events-none"
                    style={{ width: "82%", height: "82%", filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.45))" }}
                  />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
